import { Box, FormControl, FormHelperText, MenuItem, Select, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { bool, mixed, number, object, string } from 'yup';
import { useAppContext } from '~/contexts';
import { DeviceMessagingBackend, DeviceNightlyBehavior } from '~/generated/graphql';
import { capitalize } from '~/lib/string';
import { DialogForm } from '../../../../components/dialogs/components';
import type {
  DeviceList__Channel as Channel,
  DeviceList__DeviceGroup as DeviceGroup,
  DeviceList__TvBrand as TVBrand,
} from '../../queries/list.generated';
import { SCREENSHOT_INTERVALS } from '../lib/helpers';

export interface BulkDeviceSettingsFormValues {
  api: boolean | undefined;
  cacheContent: boolean | undefined;
  closedCaptioning: boolean | undefined;
  defaultChannelId: number | null | undefined;
  demo: boolean | undefined;
  deviceGroupId: number | null | undefined;
  eventStream: boolean | undefined;
  internal: boolean | undefined;
  messagingBackend: DeviceMessagingBackend | undefined;
  nightlyBehavior: DeviceNightlyBehavior | undefined;
  overscan: number | undefined;
  preload: boolean | undefined;
  realtime: boolean | undefined;
  screenshotInterval: string | null | undefined;
  silence: boolean | undefined;
  snmpService: boolean | undefined;
  tvBrandId: number | null | undefined;
}

export const bulkDeviceSettingsValidationSchema = object({
  api: bool().optional(),
  cacheContent: bool().optional(),
  closedCaptioning: bool().optional(),
  defaultChannelId: number().integer().min(1).notRequired(),
  demo: bool().optional(),
  deviceGroupId: number().notRequired(),
  eventStream: bool().optional(),
  internal: bool().optional(),
  messagingBackend: mixed<DeviceMessagingBackend>()
    .oneOf(Object.values(DeviceMessagingBackend) as DeviceMessagingBackend[])
    .optional(),
  nightlyBehavior: mixed<DeviceNightlyBehavior>()
    .oneOf(Object.values(DeviceNightlyBehavior) as DeviceNightlyBehavior[])
    .optional(),
  overscan: number().min(50).max(100).optional(),
  preload: bool().optional(),
  realtime: bool().optional(),
  screenshotInterval: string().oneOf(Object.keys(SCREENSHOT_INTERVALS)).notRequired(),
  silence: bool().optional(),
  snmpService: bool().optional(),
  tvBrandId: number().notRequired(),
});

export const useBulkDeviceSettingsFormik = (
  initialValues: BulkDeviceSettingsFormValues,
  onSubmit: (values: BulkDeviceSettingsFormValues) => void,
) => {
  return useFormik<BulkDeviceSettingsFormValues>({
    enableReinitialize: true,
    initialValues,
    onSubmit,
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema: bulkDeviceSettingsValidationSchema,
  });
};

interface BulkDeviceSettingsFormProps {
  formik: ReturnType<typeof useBulkDeviceSettingsFormik>;
  channels: ReadonlyArray<Channel>;
  deviceGroups: ReadonlyArray<DeviceGroup>;
  tvBrands: ReadonlyArray<TVBrand>;
}

export const BulkDeviceSettingsForm = ({
  formik,
  deviceGroups,
  channels,
  tvBrands,
  ...props
}: BulkDeviceSettingsFormProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const { currentUser } = useAppContext();

  const screenshotIntervalOptions = [
    { label: t('pages.devices.list.bulk_settings.no_change'), value: '' },
    { label: t('pages.devices.list.bulk_settings.disable'), value: 'none' },
    { label: t('pages.devices.list.bulk_settings.intervals.one_minute'), value: 'PT1M' },
    { label: t('pages.devices.list.bulk_settings.intervals.two_minutes'), value: 'PT2M' },
    { label: t('pages.devices.list.bulk_settings.intervals.five_minutes'), value: 'PT5M' },
    { label: t('pages.devices.list.bulk_settings.intervals.ten_minutes'), value: 'PT10M' },
    { label: t('pages.devices.list.bulk_settings.intervals.fifteen_minutes'), value: 'PT15M' },
    { label: t('pages.devices.list.bulk_settings.intervals.thirty_minutes'), value: 'PT30M' },
    { label: t('pages.devices.list.bulk_settings.intervals.sixty_minutes'), value: 'PT60M' },
  ];

  return (
    <DialogForm onSubmit={formik.handleSubmit} {...props}>
      <label>
        <span className="label">{t('common.device_form_labels.automatic_screenshots')}</span>
        <Select
          name="screenshotInterval"
          disabled={formik.isSubmitting}
          value={
            formik.values.screenshotInterval === undefined
              ? ''
              : formik.values.screenshotInterval === null
              ? 'none'
              : formik.values.screenshotInterval
          }
          onChange={(event) => {
            const value = event.target.value;
            void formik.setFieldValue(
              'screenshotInterval',
              value === '' ? undefined : value === 'none' ? null : value,
            );
          }}
          fullWidth
        >
          {screenshotIntervalOptions.map((option) => (
            <MenuItem key={option.value || 'no-change'} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </label>

      {currentNetwork.canManage.value && (
        <label>
          <span className="label">{t('common.device_form_labels.api')}</span>
          <Select
            name="api"
            disabled={formik.isSubmitting}
            value={formik.values.api !== undefined ? String(formik.values.api) : ''}
            onChange={(event) => {
              const value = event.target.value;
              void formik.setFieldValue('api', value === '' ? undefined : value === 'true');
            }}
            fullWidth
          >
            <MenuItem value="">{t('pages.devices.list.bulk_settings.no_change')}</MenuItem>
            <MenuItem value="true">{t('pages.devices.list.bulk_settings.yes')}</MenuItem>
            <MenuItem value="false">{t('pages.devices.list.bulk_settings.no')}</MenuItem>
          </Select>
        </label>
      )}

      {currentUser.admin && (
        <label>
          <span className="label">{t('common.device_form_labels.cache_content')}</span>
          <Select
            name="cacheContent"
            disabled={formik.isSubmitting}
            value={
              formik.values.cacheContent !== undefined ? String(formik.values.cacheContent) : ''
            }
            onChange={(event) => {
              const value = event.target.value;
              void formik.setFieldValue(
                'cacheContent',
                value === '' ? undefined : value === 'true',
              );
            }}
            fullWidth
          >
            <MenuItem value="">{t('pages.devices.list.bulk_settings.no_change')}</MenuItem>
            <MenuItem value="true">{t('pages.devices.list.bulk_settings.yes')}</MenuItem>
            <MenuItem value="false">{t('pages.devices.list.bulk_settings.no')}</MenuItem>
          </Select>
        </label>
      )}

      {currentNetwork.canManage.value && (
        <>
          <label>
            <span className="label">{t('common.device_form_labels.closed_captioning')}</span>
            <Select
              name="closedCaptioning"
              disabled={formik.isSubmitting}
              value={
                formik.values.closedCaptioning !== undefined
                  ? String(formik.values.closedCaptioning)
                  : ''
              }
              onChange={(event) => {
                const value = event.target.value;
                void formik.setFieldValue(
                  'closedCaptioning',
                  value === '' ? undefined : value === 'true',
                );
              }}
              fullWidth
            >
              <MenuItem value="">{t('pages.devices.list.bulk_settings.no_change')}</MenuItem>
              <MenuItem value="true">{t('pages.devices.list.bulk_settings.yes')}</MenuItem>
              <MenuItem value="false">{t('pages.devices.list.bulk_settings.no')}</MenuItem>
            </Select>
          </label>

          <label>
            <span className="label">{t('common.device_form_labels.default_channel')}</span>
            <Select
              name="defaultChannelId"
              disabled={formik.isSubmitting || channels.length === 0}
              value={
                formik.values.defaultChannelId === undefined
                  ? ''
                  : formik.values.defaultChannelId === null
                  ? 'none'
                  : formik.values.defaultChannelId
              }
              onChange={(event) => {
                const value = event.target.value;
                void formik.setFieldValue(
                  'defaultChannelId',
                  value === '' ? undefined : value === 'none' ? null : Number(value),
                );
              }}
              fullWidth
            >
              <MenuItem value="">{t('pages.devices.list.bulk_settings.no_change')}</MenuItem>
              <MenuItem value="none">{t('common.labels.none')}</MenuItem>
              {channels.map(({ id, name, number, thumbnailFile }) => (
                <MenuItem key={id} value={id}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      '& > img': {
                        mr: 2,
                        flexShrink: 0,
                        background: 'none !important',
                        boxShadow: 'none !important',
                      },
                    }}
                  >
                    {thumbnailFile.uri && (
                      <img
                        loading="lazy"
                        width="20"
                        srcSet={thumbnailFile.uri}
                        src={thumbnailFile.uri}
                        alt=""
                      />
                    )}
                    {name}
                    {number ? ` (${number})` : ''}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </label>
        </>
      )}

      {currentUser.admin && (
        <label>
          <span className="label">{t('common.device_form_labels.demo')}</span>
          <Select
            name="demo"
            disabled={formik.isSubmitting}
            value={typeof formik.values.demo === 'boolean' ? String(formik.values.demo) : ''}
            onChange={(event) => {
              const value = event.target.value;
              void formik.setFieldValue('demo', value === '' ? undefined : value === 'true');
            }}
            fullWidth
          >
            <MenuItem value="">{t('pages.devices.list.bulk_settings.no_change')}</MenuItem>
            <MenuItem value="true">{t('pages.devices.list.bulk_settings.yes')}</MenuItem>
            <MenuItem value="false">{t('pages.devices.list.bulk_settings.no')}</MenuItem>
          </Select>
        </label>
      )}

      {currentNetwork.canManage.value && (
        <>
          <label>
            <span className="label">{t('common.device_form_labels.device_group')}</span>
            <Select
              name="deviceGroupId"
              disabled={formik.isSubmitting}
              value={
                formik.values.deviceGroupId === undefined
                  ? ''
                  : formik.values.deviceGroupId === null
                  ? 'none'
                  : String(formik.values.deviceGroupId)
              }
              onChange={(event) => {
                const value = event.target.value;
                void formik.setFieldValue(
                  'deviceGroupId',
                  value === '' ? undefined : value === 'none' ? null : Number(value),
                );
              }}
              fullWidth
            >
              <MenuItem value="">{t('pages.devices.list.bulk_settings.no_change')}</MenuItem>
              <MenuItem value="none">{t('common.labels.none')}</MenuItem>
              {deviceGroups.map(({ id, name }) => (
                <MenuItem key={id} value={String(id)}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </label>

          <label>
            <span className="label">{t('common.device_form_labels.event_stream')}</span>
            <Select
              name="eventStream"
              disabled={formik.isSubmitting}
              value={
                formik.values.eventStream !== undefined ? String(formik.values.eventStream) : ''
              }
              onChange={(event) => {
                const value = event.target.value;
                void formik.setFieldValue(
                  'eventStream',
                  value === '' ? undefined : value === 'true',
                );
              }}
              fullWidth
            >
              <MenuItem value="">{t('pages.devices.list.bulk_settings.no_change')}</MenuItem>
              <MenuItem value="true">{t('pages.devices.list.bulk_settings.yes')}</MenuItem>
              <MenuItem value="false">{t('pages.devices.list.bulk_settings.no')}</MenuItem>
            </Select>
          </label>
        </>
      )}

      {currentUser.admin && (
        <label>
          <span className="label">{t('common.device_form_labels.internal')}</span>
          <Select
            name="internal"
            disabled={formik.isSubmitting}
            value={formik.values.internal !== undefined ? String(formik.values.internal) : ''}
            onChange={(event) => {
              const value = event.target.value;
              void formik.setFieldValue('internal', value === '' ? undefined : value === 'true');
            }}
            fullWidth
          >
            <MenuItem value="">{t('pages.devices.list.bulk_settings.no_change')}</MenuItem>
            <MenuItem value="true">{t('pages.devices.list.bulk_settings.yes')}</MenuItem>
            <MenuItem value="false">{t('pages.devices.list.bulk_settings.no')}</MenuItem>
          </Select>
        </label>
      )}

      {currentUser.admin && (
        <label>
          <span className="label">{t('common.device_form_labels.messaging_backend')}</span>
          <Select
            name="messagingBackend"
            disabled={formik.isSubmitting}
            value={formik.values.messagingBackend ?? ''}
            onChange={(event) => {
              const value = event.target.value;
              void formik.setFieldValue('messagingBackend', value === '' ? undefined : value);
            }}
            fullWidth
          >
            <MenuItem value="">{t('pages.devices.list.bulk_settings.no_change')}</MenuItem>
            <MenuItem value={DeviceMessagingBackend.Pubnub}>PubNub</MenuItem>
            {import.meta.env.DEV && (
              <MenuItem value={DeviceMessagingBackend.Mqtt}>MQTT (Dev Only)</MenuItem>
            )}
            <MenuItem value={DeviceMessagingBackend.AwsIot}>IoT Core</MenuItem>
          </Select>
        </label>
      )}

      {currentUser.admin && (
        <label>
          <span className="label">{t('common.device_form_labels.preload')}</span>
          <Select
            name="preload"
            disabled={formik.isSubmitting}
            value={formik.values.preload !== undefined ? String(formik.values.preload) : ''}
            onChange={(event) => {
              const value = event.target.value;
              void formik.setFieldValue('preload', value === '' ? undefined : value === 'true');
            }}
            fullWidth
          >
            <MenuItem value="">{t('pages.devices.list.bulk_settings.no_change')}</MenuItem>
            <MenuItem value="true">{t('pages.devices.list.bulk_settings.yes')}</MenuItem>
            <MenuItem value="false">{t('pages.devices.list.bulk_settings.no')}</MenuItem>
          </Select>
        </label>
      )}

      {currentNetwork.canManage.value && (
        <label>
          <span className="label">{t('common.device_form_labels.nightly_reboot_restart')}</span>
          <Select
            name="nightlyBehavior"
            disabled={formik.isSubmitting}
            value={formik.values.nightlyBehavior === undefined ? '' : formik.values.nightlyBehavior}
            onChange={(event) => {
              const value = event.target.value;
              void formik.setFieldValue('nightlyBehavior', value === '' ? undefined : value);
            }}
            fullWidth
          >
            <MenuItem value="">{t('pages.devices.list.bulk_settings.no_change')}</MenuItem>
            {Object.values(DeviceNightlyBehavior)
              .filter((value) => currentUser.admin || value !== DeviceNightlyBehavior.None)
              .map((value) => (
                <MenuItem key={value} value={value}>
                  {capitalize(value)}
                </MenuItem>
              ))}
          </Select>
        </label>
      )}

      <label>
        <span className="label">{t('common.device_form_labels.overscan')}</span>
        <TextField
          helperText={t('pages.devices.list.bulk_settings.overscan_helper')}
          name="overscan"
          disabled={formik.isSubmitting}
          type="number"
          value={formik.values.overscan ?? ''}
          onChange={(event) => {
            const value = event.target.value;
            void formik.setFieldValue('overscan', value === '' ? undefined : Number(value));
          }}
          InputProps={{ inputProps: { min: 50, max: 100 } }}
          fullWidth
        />
      </label>

      {currentNetwork.canManage.value && (
        <label>
          <span className="label">{t('common.device_form_labels.realtime')}</span>
          <Select
            name="realtime"
            disabled={formik.isSubmitting}
            value={formik.values.realtime !== undefined ? String(formik.values.realtime) : ''}
            onChange={(event) => {
              const value = event.target.value;
              void formik.setFieldValue('realtime', value === '' ? undefined : value === 'true');
            }}
            fullWidth
          >
            <MenuItem value="">{t('pages.devices.list.bulk_settings.no_change')}</MenuItem>
            <MenuItem value="true">{t('pages.devices.list.bulk_settings.yes')}</MenuItem>
            <MenuItem value="false">{t('pages.devices.list.bulk_settings.no')}</MenuItem>
          </Select>
        </label>
      )}

      {currentNetwork.canManage.value && (
        <>
          <label>
            <span className="label">{t('common.device_form_labels.silence')}</span>
            <Select
              name="silence"
              disabled={formik.isSubmitting}
              value={formik.values.silence !== undefined ? String(formik.values.silence) : ''}
              onChange={(event) => {
                const value = event.target.value;
                void formik.setFieldValue('silence', value === '' ? undefined : value === 'true');
              }}
              fullWidth
            >
              <MenuItem value="">{t('pages.devices.list.bulk_settings.no_change')}</MenuItem>
              <MenuItem value="true">{t('pages.devices.list.bulk_settings.yes')}</MenuItem>
              <MenuItem value="false">{t('pages.devices.list.bulk_settings.no')}</MenuItem>
            </Select>
          </label>

          <label>
            <span className="label">{t('common.device_form_labels.snmp_service')}</span>
            <FormControl fullWidth>
              <Select
                name="snmpService"
                disabled={formik.isSubmitting}
                value={
                  formik.values.snmpService !== undefined ? String(formik.values.snmpService) : ''
                }
                onChange={(event) => {
                  const value = event.target.value;
                  void formik.setFieldValue(
                    'snmpService',
                    value === '' ? undefined : value === 'true',
                  );
                }}
              >
                <MenuItem value="">{t('pages.devices.list.bulk_settings.no_change')}</MenuItem>
                <MenuItem disabled={!currentNetwork.snmpConfigured} value="true">
                  {t('pages.devices.list.bulk_settings.yes')}
                </MenuItem>
                <MenuItem value="false">{t('pages.devices.list.bulk_settings.no')}</MenuItem>
              </Select>
              <FormHelperText>
                {currentNetwork.snmpConfigured
                  ? t('pages.devices.list.bulk_settings.snmp_service_helper')
                  : t('pages.devices.list.bulk_settings.snmp_service_requires_community')}
              </FormHelperText>
            </FormControl>
          </label>

          <label>
            <span className="label">{t('common.device_form_labels.tv_brand')}</span>
            <Select
              name="tvBrandId"
              disabled={formik.isSubmitting}
              value={
                formik.values.tvBrandId === undefined
                  ? ''
                  : formik.values.tvBrandId === null
                  ? 'none'
                  : String(formik.values.tvBrandId)
              }
              onChange={(event) => {
                const value = event.target.value;
                void formik.setFieldValue(
                  'tvBrandId',
                  value === '' ? undefined : value === 'none' ? null : Number(value),
                );
              }}
              fullWidth
            >
              <MenuItem value="">{t('pages.devices.list.bulk_settings.no_change')}</MenuItem>
              <MenuItem value="none">{t('common.labels.none')}</MenuItem>
              {tvBrands.map(({ id, name }) => (
                <MenuItem key={id} value={String(id)}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </label>
        </>
      )}
    </DialogForm>
  );
};
